import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, StatCard, StatusPill, fpDataGridSx, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const FORM_INICIAL = {
  // Cliente
  nombres: "", apellidos: "", cedula: "", edad: "", ecivil: "", apodo: "",
  depend: "",
  // Contacto
  direccion: "", urb: "", telefono: "", celular: "", email: "",
  // Crédito
  cantsol: "", interes: "", plazo: "", fechaini: "", chasis: "", comentario: "", vendedor: "",
  // Laboral
  empresa: "", dirtrab: "", teltrab: "", tiempotrab: "",
  // Fiador
  fiador: "", fiadorced: "", fiadordir: "", urbfiad: "", fiadortel: "", edadfiad: "",
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
  if (Number.isNaN(n)) return String(v);
  return `RD$ ${n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Formatea cédula dominicana como 000-0000000-0 (progresivo mientras se escribe).
const formatCedula = (v) => {
  const d = String(v || "").replace(/\D/g, "").slice(0, 11);
  const p1 = d.slice(0, 3);
  const p2 = d.slice(3, 10);
  const p3 = d.slice(10, 11);
  let out = p1;
  if (p2) out += "-" + p2;
  if (p3) out += "-" + p3;
  return out;
};

const initials = (s) =>
  String(s || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "—";

function statusPill(status) {
  const s = String(status || "").trim().toUpperCase();
  if (s === "P") return <StatusPill label="Pendiente" variant="warning" />;
  if (s === "A") return <StatusPill label="Aceptada" variant="success" />;
  if (s === "R") return <StatusPill label="Rechazada" variant="danger" />;
  return <StatusPill label="—" variant="neutral" />;
}

// Section header for the create dialog (defined at module scope so inputs don't remount).
function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

// Estilos de botón con colores fijos (la plantilla global deja los Button de MUI
// sin fondo o con texto muy claro; con sx forzamos fondo/texto legibles).
const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", py: 6, color: FP.color.textMuted }}>
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>Sin solicitudes</Typography>
      <Typography sx={{ fontSize: "0.82rem" }}>Crea una nueva o ajusta los filtros</Typography>
    </Stack>
  );
}

export default function SolicitudesCredito() {
  const { showAlert } = useGlobalMDAlert();

  const { post } = useApi();

  // ---- Listado / filtros ----
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroUtil, setFiltroUtil] = useState("");
  const [fecha1, setFecha1] = useState("");
  const [fecha2, setFecha2] = useState("");

  const cargar = useCallback(
    async (ov = {}) => {
      setLoading(true);
      try {
        const { ok, data } = await post("/api/solicitudcredito_listar", {
          status: ov.status ?? filtroStatus,
          utilizada: ov.utilizada ?? filtroUtil,
          fecha1: ov.fecha1 ?? fecha1,
          fecha2: ov.fecha2 ?? fecha2,
        });
        if (ok && Array.isArray(data.solicitudes)) setRows(data.solicitudes);
        else showAlert({ message: data.Error || "No se pudo cargar la lista", color: "error" });
      } catch (e) {
        showAlert({ message: "Error de red o servidor", color: "error" });
      } finally {
        setLoading(false);
      }
    },
    [post, filtroStatus, filtroUtil, fecha1, fecha2, showAlert]
  );

  const limpiarFiltros = () => {
    setFiltroStatus("");
    setFiltroUtil("");
    setFecha1("");
    setFecha2("");
    cargar({ status: "", utilizada: "", fecha1: "", fecha2: "" });
  };

  // Usuarios del sistema (para el selector de vendedor)
  const [usuarios, setUsuarios] = useState([]);
  const cargarUsuarios = useCallback(async () => {
    const { ok, data } = await post("/api/solicitudcredito_usuarios", {});
    if (ok && Array.isArray(data.usuarios)) setUsuarios(data.usuarios);
  }, [post]);

  useEffect(() => {
    cargar();
    cargarUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- KPIs ----
  const kpis = useMemo(() => {
    let p = 0, a = 0, r = 0, u = 0;
    rows.forEach((row) => {
      const s = String(row.status || "").toUpperCase();
      if (s === "P") p += 1;
      else if (s === "A") a += 1;
      else if (s === "R") r += 1;
      if (String(row.utilizada || "").toUpperCase() === "S") u += 1;
    });
    return { total: rows.length, p, a, r, u };
  }, [rows]);

  // ---- Acciones ----
  const aceptar = async (id) => {
    if (!window.confirm("¿Aceptar esta solicitud?")) return;
    const { ok, data } = await post("/api/solicitudcredito_aceptar", { id });
    if (ok) {
      showAlert({ message: data.mensajeok || "Aceptada", color: "success" });
      cargar();
    } else showAlert({ message: data.Error || "Error", color: "error" });
  };

  // Rechazar
  const [rechOpen, setRechOpen] = useState(false);
  const [rechId, setRechId] = useState(null);
  const [rechMotivo, setRechMotivo] = useState("");
  const abrirRechazar = (id) => {
    setRechId(id);
    setRechMotivo("");
    setRechOpen(true);
  };
  const confirmarRechazo = async () => {
    if (!rechMotivo.trim()) {
      showAlert({ message: "Indique el motivo de rechazo", color: "warning" });
      return;
    }
    const { ok, data } = await post("/api/solicitudcredito_rechazar", {
      id: rechId,
      rechazo: rechMotivo.trim(),
    });
    if (ok) {
      showAlert({ message: data.mensajeok || "Rechazada", color: "success" });
      setRechOpen(false);
      cargar();
    } else showAlert({ message: data.Error || "Error", color: "error" });
  };

  // Marcar utilizada (vincular préstamo)
  const [utilOpen, setUtilOpen] = useState(false);
  const [utilId, setUtilId] = useState(null);
  const [opciones, setOpciones] = useState([]);
  const [selPrest, setSelPrest] = useState(null);
  const [loadingOpc, setLoadingOpc] = useState(false);
  const searchTimer = useRef(null);

  const abrirUtilizar = (id) => {
    setUtilId(id);
    setSelPrest(null);
    setOpciones([]);
    setUtilOpen(true);
  };
  const buscarPrestamos = async (q) => {
    setLoadingOpc(true);
    try {
      const { ok, data } = await post("/api/prestamosbackend_buscaractivos", { q, limit: 50 });
      if (ok && Array.isArray(data.prestamos)) setOpciones(data.prestamos);
    } finally {
      setLoadingOpc(false);
    }
  };
  const confirmarUtilizar = async () => {
    const noprest = selPrest ? selPrest.noprest ?? selPrest.NOPREST : null;
    if (!noprest) {
      showAlert({ message: "Seleccione el préstamo a vincular", color: "warning" });
      return;
    }
    const { ok, data } = await post("/api/solicitudcredito_marcarutilizada", { id: utilId, noprest });
    if (ok) {
      showAlert({ message: data.mensajeok || "Marcada como utilizada", color: "success" });
      setUtilOpen(false);
      cargar();
    } else showAlert({ message: data.Error || "Error", color: "error" });
  };
  const quitarUtilizada = async (id) => {
    if (!window.confirm("¿Marcar como NO utilizada (desvincular préstamo)?")) return;
    const { ok, data } = await post("/api/solicitudcredito_marcarutilizada", { id, revertir: true });
    if (ok) {
      showAlert({ message: data.mensajeok || "Actualizada", color: "success" });
      cargar();
    } else showAlert({ message: data.Error || "Error", color: "error" });
  };

  // Crear
  const [crearOpen, setCrearOpen] = useState(false);
  const [form, setForm] = useState(FORM_INICIAL);
  const [guardando, setGuardando] = useState(false);
  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Chasis disponibles de la sucursal
  const [chasisOpts, setChasisOpts] = useState([]);
  const [loadingChasis, setLoadingChasis] = useState(false);
  const chasisTimer = useRef(null);
  const buscarChasis = useCallback(
    async (q) => {
      setLoadingChasis(true);
      try {
        const { ok, data } = await post("/api/solicitudcredito_chasis", { q });
        if (ok && Array.isArray(data.chasis)) setChasisOpts(data.chasis);
      } finally {
        setLoadingChasis(false);
      }
    },
    [post]
  );

  const abrirCrear = () => {
    setForm(FORM_INICIAL);
    setChasisOpts([]);
    buscarChasis("");
    setCrearOpen(true);
  };
  const guardarSolicitud = async () => {
    if (!form.nombres.trim() && !form.apellidos.trim()) {
      showAlert({ message: "Indique al menos nombre o apellido", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/solicitudcredito_crear", form);
      if (ok) {
        showAlert({ message: data.mensajeok || "Solicitud creada", color: "success" });
        setCrearOpen(false);
        cargar();
      } else showAlert({ message: data.Error || "Error", color: "error" });
    } catch (e) {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardando(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "nosolic",
        headerName: "Solic #",
        width: 90,
        renderCell: (p) => (
          <Typography sx={{ marginTop: "15px",fontWeight: 800, fontSize: "0.86rem", color: FP.color.navy, fontVariantNumeric: "tabular-nums" }}>
            {p.value || "—"}
          </Typography>
        ),
      },
      { field: "fecha", headerName: "Fecha", width: 110 },
      {
        field: "cliente",
        headerName: "Cliente",
        flex: 1,
        minWidth: 220,
        valueGetter: (value, row) => `${row.nombres || ""} ${row.apellidos || ""}`.trim(),
        renderCell: (params) => {
          const nombre = params.value || "—";
          return (
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0, marginTop: "5px" }}>
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                }}
              >
                {initials(nombre)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{textAlign: "left",fontWeight: 600, fontSize: "0.86rem", color: FP.color.textPrimary }}>
                  {nombre}
                </Typography>
                {params.row.cedula ? (
                  <Typography noWrap sx={{textAlign: "left", fontSize: "0.72rem", color: FP.color.textMuted }}>
                    {formatCedula(params.row.cedula)}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          );
        },
      },
      {
        field: "cantsol",
        headerName: "Monto",
        width: 130,
        align: "right",
        headerAlign: "right",
        renderCell: (p) => (
          <Typography sx={{ marginTop: "15px",fontWeight: 600,fontSize: "0.86rem", fontVariantNumeric: "tabular-nums", color: FP.color.textPrimary }}>
            {fmtMoney(p.value)}
          </Typography>
        ),
      },
      { field: "plazo", headerName: "Plazo", width: 80, align: "center", headerAlign: "center" },
      {
        field: "interes",
        headerName: "Interés",
        width: 90,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) => (value == null || value === "" ? "—" : `${value}%`),
      },
      {
        field: "vendedor",
        headerName: "Vendedor",
        width: 150,
        renderCell: (p) =>
          p.value ? (
            <Chip
              size="small"
              icon={<PersonOutlineIcon sx={{ fontSize: 15 }} />}
              label={p.value}
              sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 600 }}
            />
          ) : (
            <Typography sx={{ color: FP.color.textMuted }}>—</Typography>
          ),
      },
      {
        field: "status",
        headerName: "Estado",
        width: 130,
        renderCell: (params) => {
          const pill = statusPill(params.value);
          if (String(params.value).toUpperCase() === "R" && params.row.rechazo)
            return (
              <Tooltip title={`Motivo: ${params.row.rechazo}`} arrow>
                <span>{pill}</span>
              </Tooltip>
            );
          return pill;
        },
      },
      {
        field: "utilizada",
        headerName: "Utilizada",
        width: 130,
        renderCell: (params) =>
          String(params.value).toUpperCase() === "S" ? (
            <Tooltip title={`Préstamo #${params.row.noprest}`} arrow>
              <span>
                <StatusPill label={`Sí · #${params.row.noprest}`} variant="info" />
              </span>
            </Tooltip>
          ) : (
            <StatusPill label="No" variant="neutral" />
          ),
      },
      {
        field: "acciones",
        headerName: "Acciones",
        width: 250,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const s = String(params.row.status || "").toUpperCase();
          const utilizada = String(params.row.utilizada || "").toUpperCase() === "S";
          return (
            <Stack direction="row" spacing={1} sx={{ marginTop: "10px" }}>
              {s === "P" && (
                <>
                  <Button size="small" disableElevation sx={btnSolid(FP.color.success, FP.color.successDark)} onClick={() => aceptar(params.row.id)}>
                    Aceptar
                  </Button>
                  <Button size="small" sx={btnOutline(FP.color.danger)} onClick={() => abrirRechazar(params.row.id)}>
                    Rechazar
                  </Button>
                </>
              )}
              {s === "A" && !utilizada && (
                <Button size="small" disableElevation startIcon={<LinkIcon />} sx={btnSolid(FP.color.accentBlue, FP.color.navy)} onClick={() => abrirUtilizar(params.row.id)}>
                  Utilizar
                </Button>
              )}
              {s === "A" && utilizada && (
                <Button size="small" sx={btnOutline(FP.color.warning)} onClick={() => quitarUtilizada(params.row.id)}>
                  Quitar uso
                </Button>
              )}
              {s === "R" && (
                <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted, fontStyle: "italic" }}>
                  Sin acciones
                </Typography>
              )}
            </Stack>
          );
        },
      },
    ].map((c) => ({ ...c, align: "center", headerAlign: "center" })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Solicitudes de Crédito"
        subtitle="Crea, aprueba o rechaza solicitudes y vincúlalas a un préstamo"
        cta={
          <Button startIcon={<AddIcon />} onClick={abrirCrear} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
            Nueva solicitud
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* KPIs */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatCard label="Total" value={kpis.total} icon={DescriptionOutlinedIcon} accent={FP.color.navy} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Pendientes" value={kpis.p} icon={HourglassEmptyIcon} accent={FP.color.warning} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Aceptadas" value={kpis.a} icon={CheckCircleOutlineIcon} accent={FP.color.success} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Utilizadas" value={kpis.u} icon={VerifiedOutlinedIcon} accent={FP.color.info} />
          </Grid>
        </Grid>

        {/* Filtros */}
        <Card elevation={0} sx={{ mb: 3, borderRadius: 2.5, border: `1px solid ${FP.color.border}`, boxShadow: FP.shadow.soft }}>
          <CardContent sx={{ p: { xs: 2, md: 2.6 } }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <FilterAltOutlinedIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
              <Typography sx={{ fontWeight: 800, fontSize: "0.74rem", letterSpacing: "0.1em", textTransform: "uppercase", color: FP.color.textSecondary }}>
                Filtros
              </Typography>
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField select fullWidth label="Estado" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} sx={fpInputSx}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="P">Pendientes</MenuItem>
                  <MenuItem value="A">Aceptadas</MenuItem>
                  <MenuItem value="R">Rechazadas</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField select fullWidth label="Utilizada" value={filtroUtil} onChange={(e) => setFiltroUtil(e.target.value)} sx={fpInputSx}>
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="S">Sí</MenuItem>
                  <MenuItem value="N">No</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField fullWidth type="date" label="Desde" InputLabelProps={{ shrink: true }} value={fecha1} onChange={(e) => setFecha1(e.target.value)} sx={fpInputSx} />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField fullWidth type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={fecha2} onChange={(e) => setFecha2(e.target.value)} sx={fpInputSx} />
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Stack direction="row" spacing={1}>
                  <Button fullWidth startIcon={<SearchIcon />} onClick={() => cargar()} disabled={loading} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
                    Buscar
                  </Button>
                  <Tooltip title="Limpiar filtros">
                    <Button onClick={limpiarFiltros} sx={{ ...btnOutline(FP.color.navy), minWidth: 44, px: 0 }}>
                      <RestartAltIcon />
                    </Button>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabla */}
        <Card elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${FP.color.border}`, boxShadow: FP.shadow.soft, overflow: "hidden" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.8, borderBottom: `1px solid ${FP.color.border}` }}>
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>Listado de solicitudes</Typography>
            <Chip
              size="small"
              label={`${rows.length} ${rows.length === 1 ? "registro" : "registros"}`}
              sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }}
            />
          </Stack>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              getRowId={(r) => r.id}
              loading={loading}
              rowHeight={56}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "260px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Card>
      </Box>

      {/* Dialog Rechazar */}
      <Dialog open={rechOpen} onClose={() => setRechOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <HighlightOffIcon sx={{ color: FP.color.danger }} />
          Rechazar solicitud
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary, mb: 2 }}>
            Esta acción marca la solicitud como rechazada. Indica el motivo para dejar constancia.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Motivo de rechazo"
            value={rechMotivo}
            onChange={(e) => setRechMotivo(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRechOpen(false)} sx={btnGhost}>Cancelar</Button>
          <Button disableElevation startIcon={<HighlightOffIcon />} onClick={confirmarRechazo} sx={btnSolid(FP.color.danger, "#BE123C")}>
            Rechazar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Marcar utilizada */}
      <Dialog open={utilOpen} onClose={() => setUtilOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <LinkIcon sx={{ color: FP.color.accentBlue }} />
          Vincular a un préstamo
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary, mb: 2 }}>
            Selecciona el préstamo activo que utiliza esta solicitud. Quedará marcada como utilizada.
          </Typography>
          <Autocomplete
            options={opciones}
            loading={loadingOpc}
            value={selPrest}
            getOptionLabel={(o) => (o ? `#${o.noprest ?? o.NOPREST} — ${o.cliente || ""}` : "")}
            isOptionEqualToValue={(o, v) => (o.noprest ?? o.NOPREST) === (v.noprest ?? v.NOPREST)}
            onInputChange={(_, value, reason) => {
              if (reason === "input") {
                clearTimeout(searchTimer.current);
                searchTimer.current = setTimeout(() => buscarPrestamos(value.trim()), 280);
              }
            }}
            onChange={(_, opt) => setSelPrest(opt)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar préstamo activo"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setUtilOpen(false)} sx={btnGhost}>Cancelar</Button>
          <Button disableElevation startIcon={<CheckCircleOutlineIcon />} onClick={confirmarUtilizar} sx={btnSolid(FP.color.accentBlue, FP.color.navy)}>
            Marcar utilizada
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Crear */}
      <Dialog open={crearOpen} onClose={() => setCrearOpen(false)} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3, width: "100%", maxWidth: 1160 } }}>
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <DescriptionOutlinedIcon sx={{ color: FP.color.navy }} />
          Nueva solicitud de crédito
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: FP.color.surface, p: { xs: 2, sm: 3 } }}>
          <Box sx={sectionBox}>
            <SectionTitle icon={PersonOutlineIcon} color={FP.color.accentBlue}>Cliente</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Nombres" value={form.nombres} onChange={setF("nombres")} sx={fpInputSx} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Apellidos" value={form.apellidos} onChange={setF("apellidos")} sx={fpInputSx} /></Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Cédula" value={form.cedula}
                  onChange={(e) => setForm((p) => ({ ...p, cedula: formatCedula(e.target.value) }))}
                  placeholder="000-0000000-0" inputProps={{ maxLength: 13 }} sx={fpInputSx} />
              </Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth type="number" label="Edad" value={form.edad} onChange={setF("edad")} sx={fpInputSx} /></Grid>
              <Grid item xs={6} sm={3}>
                <TextField select fullWidth label="Estado civil" value={form.ecivil} onChange={setF("ecivil")} sx={fpInputSx}>
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="Soltero">Soltero</MenuItem>
                  <MenuItem value="Casado">Casado</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="Apodo" value={form.apodo} onChange={setF("apodo")} sx={fpInputSx} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth type="number" label="Dependientes" value={form.depend} onChange={setF("depend")} sx={fpInputSx} /></Grid>
            </Grid>
          </Box>

          <Box sx={sectionBox}>
            <SectionTitle icon={PhoneOutlinedIcon} color={FP.color.info}>Contacto</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Dirección" value={form.direccion} onChange={setF("direccion")} sx={fpInputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Urbanización / Sector" value={form.urb} onChange={setF("urb")} sx={fpInputSx} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Teléfono" value={form.telefono} onChange={setF("telefono")} sx={fpInputSx} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Celular" value={form.celular} onChange={setF("celular")} sx={fpInputSx} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Email" value={form.email} onChange={setF("email")} sx={fpInputSx} /></Grid>
            </Grid>
          </Box>

          <Box sx={sectionBox}>
            <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>Crédito</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth type="number" label="Monto solicitado" value={form.cantsol} onChange={setF("cantsol")} sx={fpInputSx}
                  InputProps={{ startAdornment: <InputAdornment position="start">RD$</InputAdornment> }} />
              </Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth type="number" label="Plazo (cuotas)" value={form.plazo} onChange={setF("plazo")} sx={fpInputSx} /></Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth type="number" label="Tasa interés mensual" value={form.interes} onChange={setF("interes")} sx={fpInputSx}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
              </Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth type="date" label="Fecha inicio" InputLabelProps={{ shrink: true }} value={form.fechaini} onChange={setF("fechaini")} sx={fpInputSx} /></Grid>
              <Grid item xs={6} sm={3}>
                <Autocomplete
                  freeSolo
                  options={chasisOpts}
                  loading={loadingChasis}
                  value={form.chasis || null}
                  getOptionLabel={(o) => (typeof o === "string" ? o : o.chasis || "")}
                  isOptionEqualToValue={(o, v) => (o.chasis || o) === (v.chasis || v)}
                  renderOption={(props, o) => (
                    <li {...props} key={o.chasis}>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>{o.chasis}</Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                          {[o.marca, o.modelo, o.colore].filter(Boolean).join(" · ")}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  onChange={(_, value) =>
                    setForm((p) => ({ ...p, chasis: typeof value === "string" ? value : value?.chasis || "" }))
                  }
                  onInputChange={(_, value, reason) => {
                    if (reason === "input") {
                      clearTimeout(chasisTimer.current);
                      chasisTimer.current = setTimeout(() => buscarChasis(value.trim()), 280);
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Chasis" sx={fpInputSx} />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={usuarios}
                  value={form.vendedor || null}
                  onChange={(_, value) => setForm((p) => ({ ...p, vendedor: value || "" }))}
                  isOptionEqualToValue={(o, v) => o === v}
                  renderInput={(params) => (
                    <TextField {...params} label="Vendedor (usuario del sistema)" sx={fpInputSx}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                          </InputAdornment>
                        ),
                      }} />
                  )}
                />
              </Grid>
              <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Comentario" value={form.comentario} onChange={setF("comentario")} /></Grid>
            </Grid>
          </Box>

          <Box sx={sectionBox}>
            <SectionTitle icon={WorkOutlineIcon} color={FP.color.warning}>Laboral</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Empresa" value={form.empresa} onChange={setF("empresa")} sx={fpInputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Dirección trabajo" value={form.dirtrab} onChange={setF("dirtrab")} sx={fpInputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Teléfono trabajo" value={form.teltrab} onChange={setF("teltrab")} sx={fpInputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Tiempo en trabajo (años)" value={form.tiempotrab} onChange={setF("tiempotrab")} sx={fpInputSx} /></Grid>
            </Grid>
          </Box>

          <Box sx={{ ...sectionBox, mb: 0 }}>
            <SectionTitle icon={AssignmentIndOutlinedIcon} color={FP.color.danger}>Fiador</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Nombre fiador" value={form.fiador} onChange={setF("fiador")} sx={fpInputSx} /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Cédula fiador" value={form.fiadorced}
                  onChange={(e) => setForm((p) => ({ ...p, fiadorced: formatCedula(e.target.value) }))}
                  placeholder="000-0000000-0" inputProps={{ maxLength: 13 }} sx={fpInputSx} />
              </Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Dirección fiador" value={form.fiadordir} onChange={setF("fiadordir")} sx={fpInputSx} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth label="Urb. fiador" value={form.urbfiad} onChange={setF("urbfiad")} sx={fpInputSx} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="Tel. fiador" value={form.fiadortel} onChange={setF("fiadortel")} sx={fpInputSx} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth type="number" label="Edad fiador" value={form.edadfiad} onChange={setF("edadfiad")} sx={fpInputSx} /></Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.2 }}>
          <Button onClick={() => setCrearOpen(false)} sx={btnGhost}>Cancelar</Button>
          <Button disableElevation startIcon={<AddIcon />} onClick={guardarSolicitud} disabled={guardando} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
            {guardando ? "Guardando…" : "Guardar solicitud"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
